import { useTranslation } from "@/packages/utils";
import { Button, Badge } from "@/components/ui";
import { CustomerPortalLink } from "@convex-dev/polar/react";
import { api } from "convex/_generated/api";
import { PRICING } from "@/packages/features-billing";
import type { BillingTier } from "@/packages/domains/billing";
import type { PaidTier } from "@/packages/features-billing";

interface PlanCardProps {
  tier: BillingTier;
  subscription: {
    tier: BillingTier;
    status: string;
    productId?: string;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd?: boolean;
    billingInterval?: "monthly" | "yearly" | null;
    trialDaysRemaining?: number | null;
  } | null;
}

export function PlanCard({ subscription, tier }: PlanCardProps) {
  const { t } = useTranslation(["settings"]);

  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;
  const currentPeriodEnd = subscription?.currentPeriodEnd;
  const trialDaysRemaining = subscription?.trialDaysRemaining ?? null;
  const isTrialActive = trialDaysRemaining !== null && trialDaysRemaining > 0;

  const tierLabels: Record<BillingTier, string> = {
    free: t("settings:billing.pricing.tierEssentials"),
    essentials: t("settings:billing.pricing.tierEssentials"),
    professional: t("settings:billing.pricing.tierProfessional"),
    unlimited: t("settings:billing.pricing.tierUnlimited"),
  };

  const billingInterval = subscription?.billingInterval ?? null;
  const paidTier: PaidTier =
    tier === "free" ? "essentials" : (tier as PaidTier);
  const interval = billingInterval ?? "yearly";
  const price = PRICING[paidTier]?.[interval]?.price;

  const intervalLabel =
    interval === "monthly"
      ? t("settings:billing.pricing.billingMonthly")
      : t("settings:billing.pricing.billingYearly");

  const nextBillingDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="rounded-card border border-border p-5 bg-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[16px] font-semibold text-primary">
              {tierLabels[tier]}
            </h3>
            <Badge variant="status" status="active">
              {t("settings:billing.pricing.activeBadge")}
            </Badge>
            {isTrialActive && (
              <Badge
                color={{
                  bg: "rgba(245,158,11,0.12)",
                  text: "rgb(180,115,10)",
                }}
              >
                {t("settings:billing.pricing.trialBadgeShort", {
                  days: trialDaysRemaining,
                })}
              </Badge>
            )}
          </div>
          {price && (
            <p className="text-[13px] text-secondary mt-1">
              {`\u20AC${price} ${intervalLabel}`}
            </p>
          )}
          {subscription?.status === "past_due" && (
            <p className="text-[13px] text-red-600 dark:text-red-400 mt-1">
              {t("settings:billing.pastDueStatus")}
            </p>
          )}
        </div>
        {tier !== "free" && (
          <CustomerPortalLink
            polarApi={{
              generateCustomerPortalUrl: api.polar.generateCustomerPortalUrl,
            }}
          >
            <Button variant="primary" size="sm">
              {t("settings:billing.manageSubscription")}
            </Button>
          </CustomerPortalLink>
        )}
      </div>

      {cancelAtPeriodEnd && nextBillingDate && (
        <div className="mb-3 rounded-lg bg-warning-soft px-3 py-2">
          <p className="text-[12px] font-medium text-warning">
            {t("settings:billing.cancelsOn", { date: nextBillingDate })}
          </p>
        </div>
      )}

      {nextBillingDate && !cancelAtPeriodEnd && (
        <p className="text-[12px] text-muted">
          {t("settings:billing.nextBillingDate", { date: nextBillingDate })}
        </p>
      )}
    </div>
  );
}
